import React from "react";
import copy from "copy-to-clipboard";
import { toast } from "sonner";
import { Copy, ExternalLink, Monitor, Smartphone } from "lucide-react";
import { Button, Combobox, Label, Tabs, Tooltip } from "@/components/ui";
import { cn } from "@/lib/utils";
import { PreviewDevice, PreviewPlayerState } from "./usePreviewPlayer";

const copyText = (value: string, copiedMessage: string) => {
  if (!value) {
    toast.error("Not available yet");
    return;
  }
  copy(value);
  toast.success(copiedMessage);
};

interface CopyButtonProps {
  value: string;
  label: string;
  copiedMessage: string;
  variant?: React.ComponentProps<typeof Button>["variant"];
  size?: React.ComponentProps<typeof Button>["size"];
}

const CopyButton: React.FC<CopyButtonProps> = ({ value, label, copiedMessage, variant = "outline", size }) => (
  <Button type="button" variant={variant} size={size} onClick={() => copyText(value, copiedMessage)}>
    <Copy />
    {label}
  </Button>
);

const AddressBarButton: React.FC<{ label: string; onClick: () => void; children: React.ReactNode }> = ({
  label,
  onClick,
  children,
}) => (
  <Tooltip>
    <Tooltip.Trigger
      render={
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          className="text-slate-500 hover:bg-slate-200 hover:text-slate-800"
          onClick={onClick}
          aria-label={label}
        >
          {children}
        </Button>
      }
    />
    <Tooltip.Content>{label}</Tooltip.Content>
  </Tooltip>
);

const PageLinkActions: React.FC<{ url: string; isTemplate: boolean }> = ({ url, isTemplate }) => (
  <>
    <AddressBarButton
      label={isTemplate ? "Copy link template" : "Copy link"}
      onClick={() => copyText(url, isTemplate ? "Link template copied" : "Link copied")}
    >
      <Copy />
    </AddressBarButton>
    {!isTemplate && (
      <AddressBarButton label="Open page" onClick={() => window.open(url, "_blank")}>
        <ExternalLink />
      </AddressBarButton>
    )}
  </>
);

const PreviewToolbar: React.FC<{ preview: PreviewPlayerState }> = ({ preview }) => (
  <div className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2">
    <Tabs value={preview.previewDevice} onValueChange={next => preview.setPreviewDevice(next as PreviewDevice)}>
      <Tabs.List aria-label="Preview device">
        <Tabs.Trigger value="web" className="gap-1.5 px-3">
          <Monitor className="size-3.5" />
          Web
        </Tabs.Trigger>
        <Tabs.Trigger value="mobile" className="gap-1.5 px-3">
          <Smartphone className="size-3.5" />
          Mobile
        </Tabs.Trigger>
      </Tabs.List>
    </Tabs>

    <div className="flex min-w-0 items-center gap-2">
      <Label className="shrink-0 text-slate-500">Preview as</Label>
      <div className="w-60 min-w-0">
        <Combobox
          {...preview.players}
          showClear
          placeholder="Sample data"
          emptyText="No Players found"
          itemToStringLabel={player => player.fullName || player.email || "No Name"}
          itemToLabel={player => (
            <>
              <span className="font-semibold ignore-base-styling">{player.fullName || "No Name"}</span>
              {player.email ? ` - ${player.email}` : ""}
            </>
          )}
        />
      </div>
    </div>
  </div>
);

const MOBILE_BACKDROP_CLASSES = "bg-slate-100 p-4";
const MOBILE_DEVICE_CLASSES = "rounded-2xl shadow-lg ring-1 ring-slate-300";

interface PreviewFrameProps {
  address: React.ReactNode;
  addressActions?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}

const PreviewFrame: React.FC<PreviewFrameProps> = ({ address, addressActions, className, children }) => (
  <div
    className={cn(
      "flex min-w-0 w-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-slate-50",
      className
    )}
  >
    <div className="flex shrink-0 items-center gap-3 border-b border-slate-200 bg-slate-100 px-3 py-2">
      <div className="flex gap-1.5" aria-hidden>
        <span className="size-2.5 rounded-full bg-slate-300" />
        <span className="size-2.5 rounded-full bg-slate-300" />
        <span className="size-2.5 rounded-full bg-slate-300" />
      </div>
      <div className="min-w-0 flex-1 truncate rounded-md bg-white px-3 py-1 text-xs text-slate-500">{address}</div>
      {addressActions && <div className="flex shrink-0 items-center gap-0.5">{addressActions}</div>}
    </div>
    {children}
  </div>
);

export {
  CopyButton,
  MOBILE_BACKDROP_CLASSES,
  MOBILE_DEVICE_CLASSES,
  PageLinkActions,
  PreviewFrame,
  PreviewToolbar,
  copyText,
};
